/* tslint:disable:import-spacing align no-trailing-whitespace typedef-whitespace variable-name */

import { DockNode       }        from 'dock-spawn-ts/lib/js/DockNode';
import { DockManager    }        from 'dock-spawn-ts/lib/js/DockManager';
import { DockConfig     }        from 'dock-spawn-ts/lib/js/DockConfig';

import { IdeService            } from '@/services/ide.service';
import { ServiceManager        } from "@/services/service.manager";

import { DocumentMetadata }      from './document.metadata';
import { DocumentWindowFactory } from './document.window.factory';
import { DocumentWindow   }      from './document.window';
import { Logger           }      from '@/util/logger';
import {
  Window,
  WindowType
}                                from './window';
import {
  BehaviorSubject,
  Observable
}                                from 'rxjs';

import * as $                    from 'jquery';


/** helper class to tie together a window and its launch parameters */
export class DocumentTab {

    public meta: DocumentMetadata;
  public window: DocumentWindow;


  constructor(
       meta: DocumentMetadata,
     window: DocumentWindow
  ) {

    this.meta   = meta;
    this.window = window;
  }
}

/**
 *
 * @class DocumentWindowManager
 *
 * @brief this is the main controller for the tabbed views in the center of the GUI.  We track the
 * active tabs along with the history and allow for launching/injecting new tabs, as well as performing
 * operations on existing active tabs.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
export class DocumentWindowManager {

  /** access to the various services */
  private services:  ServiceManager      = null;

  /** access to the ide service */
  private                ide: IdeService = null;

  /** support for publishing notifications of change for tab set */
  private historySubject:        BehaviorSubject<DocumentTab[]>;
  private historyObservable:     Observable<DocumentTab[]>;

  /** support for publishing close of tab */
  private closeTabSubject:        BehaviorSubject<DocumentTab>;
  private closeTabObservable:     Observable<DocumentTab>;

  /** support for publishing open of tab */
  private openTabSubject:        BehaviorSubject<DocumentTab>;
  private openTabObservable:     Observable<DocumentTab>;

  /** the list of tabbed windows we are managing */
  protected tabs:  DocumentTab[] = [];

  /** config for DockSpawn, interactions for save/restore GUI layout */
  private config:     DockConfig = null;

  /** the DockSpawn manager */
  private dockSpawn: DockManager = null;

  /** track timeout so we can cancel if we are overlapping */
  private           _renderTimer = null;

  public anchorSub: BehaviorSubject<{kind: WindowType, window: DocumentWindow}> =
    new BehaviorSubject<{kind: WindowType, window: DocumentWindow}>({kind: WindowType.None, window: null});

  /**
   *
   * some kinds of windows can have a default or "anchor" window, which is the window
   * to send actions to as the "active" window for that group.  Such windows will have
   * an anchor button at the top that allows the user to pick which window (of that kind)
   * is the default window to work with.
   *
   * This enables us to do things like add multiple chunks of data to the same window
   * from other parts of the GUI, so you can do things like compare data side by side etc.
   *
   */
  private anchors: Map<WindowType, DocumentWindow> = new Map<WindowType, DocumentWindow>();

  /** standard constructor */
  constructor(config: DockConfig, dockSpawn: DockManager, services: ServiceManager) {

    Logger.debug('[DocumentWindowManager] setting up....');

    this.config      = config;
    this.dockSpawn   = dockSpawn;
    this.services    = services;
    this.ide         = services.ide;

    /* setup observable support on the tab list */
    this.historySubject        = new BehaviorSubject<DocumentTab[]>(null);
    this.historyObservable     = this.historySubject.asObservable();

    this.closeTabSubject        = new BehaviorSubject<DocumentTab>(null);
    this.closeTabObservable     = this.closeTabSubject.asObservable();

    this.openTabSubject        = new BehaviorSubject<DocumentTab>(null);
    this.openTabObservable     = this.openTabSubject.asObservable();
  }

  /** fetch the blob service */
  public blobService() {

    return this.services.blobber;
  }

  /** fetch the blob service */
  public authService() {

    return this.services.auth;
  }

  /** fetch the blob service */
  public ideService() {

    return this.ide;
  }

  /** fetch the blob service */
  public modalsService() {

    return this.services.modals;
  }

  /** fetch the task update service */
  public taskProgressService() {

    return this.services.taskMonitor;
  }

  public serviceManager(): ServiceManager {

    return this.services;
  }

  public rootLayout(): DockNode {

    return this.dockSpawn.context.model.documentManagerNode;
  }

  /** fetch the raw layout manager */
  public layoutManager(): DockManager {

    return this.dockSpawn;
  }

  /** fetch the windows being managed */
  public windows(): Window[] {

    return this.tabs.map(x => x.window);
  }

  /** set the anchor (default) window for this kind of window */
  public dropAnchor(kind: WindowType, window: DocumentWindow) {

    Logger.debug(`[DocumentWindowManager] new anchor (${kind})`, window);

    this.anchors.set(kind, window);

    this.anchorSub.next({kind, window});
  }

  /** return the anchor window for this kind of window (if there is one set) */
  public anchor(kind: WindowType): DocumentWindow {

    if (this.anchors.has(kind)) {

      return this.anchors.get(kind);
    }

    return null;
  }

  /** clear the anchor/default window for this kind of window */
  public raiseAnchor(kind: WindowType) {

    Logger.debug(`[DocumentWindowManager] clearing anchor (${kind}).`);

    this.anchors.delete(kind);

    this.anchorSub.next({kind, window: null});
  }

  /** wait for a new tab hosting <div> to be ready, then launch its tab content */
  public panelLaunch(params: DocumentMetadata): void {

    let dwm: DocumentWindowManager = this;
    let id: string = `#${params.id}`;

    Logger.debug(`[DocumentWindowManager] panelLaunch - launching tab ${id}`);

    function waitForRender(time: DOMHighResTimeStamp): void {

      /*
       * instead of trying to do a timeout on the existence of the DOM, we check
       * if it exists AND has a rendered geometry...if not we just request an
       * animation frame. It will typically only take a couple of frames before
       * it gets rendered; Javascript is single threaded, so Angular or whatever
       * will do all of its jazz, and this element will popup in the next frame.
       *
       */

      if ($(id).length == 0) {

        /* the window container has not even been created */

        window.requestAnimationFrame(waitForRender);

        /* keep waiting */

        return ;
      }

      let el: HTMLElement = $(id).get()[0];

      /* the tab's host container is good to go! */

      Logger.debug('[DocumentWindowManager] panelLaunch - tab host is ready: ', el);

      /* create the actual tab ... */

      const w          = DocumentWindowFactory.fromMeta(params, dwm);

      if (!w.isReady()) {

        /* some kind of weird exception or error, the Window can't be used */

        Logger.error('[DocumentWindowManager] panelLaunch - can not create tab, Window failed to be ready.');

        return ;
      }

      const t: DocumentTab = new DocumentTab(params, w);

      dwm.tabs.push(t);

      dwm.dockSpawn.dockFill(dwm.rootLayout(), t.window.panel());

      dwm.focusTab(params.id);

      Logger.debug('[DocumentWindowManager] panelLaunch - opened tab: ', params.id);

      dwm.openTabSubject.next(t);
    }

    window.requestAnimationFrame(waitForRender);
  }

  /** add a tab */
  public launch(params: DocumentMetadata): void {

    let dwm: DocumentWindowManager = this;

    Logger.debug(`[DocumentWindowManager] launch - creating new host tab: `, params);

    /* check for tabs that are singletons ... */

    if (params.type === WindowType.Jobs) {

      /* these tabs should only be one at a time, if we already have one...just focus on it. */

      if (dwm.autoRaise(params.type)) {

        /* no action required */

        return ;
      }
    }

    /* make sure we have a parent window */

    const divDockContainer: JQuery<HTMLElement> = $('#dock_div');

    if (!divDockContainer) {

      Logger.error('[DocumentWindowManager] launch - can not find dock spawn div.');

      return;
    }

    /*
     * insert a new DOM node, which will be the tab we are adding to the
     * parent window.  Typically, the parent window is one of the main
     * areas of the top level IDE; the left side, the middle or the right
     * side.  Each one can have 1 or more tabs.  Our various window types,
     * such as the File of Scenario Browser, are just tabs we "append" to
     * one of these top level windows.
     *
     * A "tab" is just a <div> that we dynamically insert.  We then call
     * call our own Window subclass to do its thing...once the tab has
     * been rendered by the browser.
     *
     */

    /* first, insert the <div> that will contain the new tab contents... */

    params.id = 'tabbed_window_' + dwm.tabs.length as string;

    $(divDockContainer).append(`<div id="${params.id}"
 data-panel-caption="${params.title}"
 class="gui-document-host editor-host"
hidden></div>`);

    dwm.panelLaunch(params);
  }

  /** ensure this window is visible */
  public raiseWindow(window: DocumentWindow): boolean {

    const existing: DocumentTab = this.tabs.find(x => x.window.id() === window.id());

    if (!existing) {

      return false;
    }

    /* raise it */

    this.focusTab(existing.meta.id);

    return true;
  }

  /** if we already hav a tab of this kind, focus on it, and return true. */
  public autoRaise(type: WindowType): boolean {

    Logger.debug('[DocumentWindowManager] autoRaise: ', type);

    const existing: DocumentTab = this.tabs.find((x: DocumentTab): boolean => x.meta.type === type);

    if (!existing) {

      return false;
    }

    /* raise it */

    this.focusTab(existing.meta.id);

    return true;
  }

  /** close the given tab */
  public closeTab(id: string, implied: boolean) {

    Logger.debug('[DocumentWindowManager] closing tab: ', id);

    /* find the tab */

    const item: DocumentTab = this.tab(id);

    if (!item) {

      Logger.error('[DocumentWindowManager] (close) no such tab: ', id);
      return ;
    }

    this.closeTabSubject.next(item);

    /* close it... */

    this.tabs = this.tabs.filter(x => x.meta.id !== id);

    /* if there was an anchor, remove the anchor as well */

    const anchor = [...this.anchors.entries()].find((x: [WindowType, DocumentWindow]) => x[1].id() == item.window.id());

    if (anchor) {

      this.anchors.delete(anchor[0]);
    }

    /* item.window.close(implied); */

  }

  /** fetch the active tab */
  public activeTab(): Window {

    const tab: DocumentTab = this.tabs.find(x => x.window.panel().tabPage.selected);

    if ((tab === null) || (typeof tab === 'undefined')) {

      return null;
    }

    return tab.window;
  }

  /** make this tab the active/visible tab */
  public focusTab(id: string) {

    /* find the tab */

    const item: DocumentTab = this.tab(id);

    if (!item) {

      Logger.error('[DocumentWindowManager] (focus) no such tab: ', id);
      return ;
    }

    Logger.debug(`[DocumentWindowManager] (${item.meta.title}|${item.meta.id}) making visible...`);

    item.window.panel().tabPage.onSelected();

  }

  /** fetch an existing tab */
  public tab(id: string) : DocumentTab {

    return this.tabs.find(x => x.meta.id === id);
  }

  /** fetch all existing tabs of the given kind */
  public tabKind(type: WindowType): DocumentWindow[] {

    return this.tabs.filter(x => x.meta.type === type).map(x => x.window);
  }

  /* fetch the active tabs */
  public currentTabs() : DocumentTab[] {

    return this.tabs;
  }

  /** watch for closing of tabs */
  public onTabClose(): Observable<DocumentTab> {

    return this.closeTabObservable;
  }

  /** watch for opening of tabs */
  public onTabOpen(): Observable<DocumentTab> {

    return this.openTabObservable;
  }

  /** fetch the user's tab list..but as an Observable */
  public activeTabs(): Observable<DocumentTab[]> {

    return this.historyObservable;
  }

}
